import {
  ACTION_MOBILE_NAV_MENU,
  ACTION_MOBILE_NAV_MENU_DESTINATION,
  CATEGORY_NAV,
} from 'utils/analytics/eventTypesConstants';
import { ChronoUnit, LocalDateTime } from 'js-joda';
import {
  Divider,
  Drawer,
  List,
  ListHeading,
  ListItem,
  SiteMenu,
  SiteMenuBackButton,
  SiteMenuTopLevelHeading,
  baseMenuId,
} from 'omakase';
import {
  IconArrowRightSmall,
  IconDelivery as IconDeliveryLegacy,
  IconDeliveryScooter,
  IconEvents,
  IconPrivateDining,
} from 'ui/icons';
import {
  IconChevronSmallRight,
  IconDelivery,
  IconExternalLink,
  IconPlate,
  IconTicket,
  IconToGo,
} from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { ListItemElementEndProps } from 'omakase/ListItem/ListItemCore';
import {
  TOCK_FACEBOOK_URL,
  TOCK_INSTAGRAM_URL,
  TOCK_LINKED_IN_URL,
  TOCK_TWITTER_URL,
} from 'constants/social';
import { buildSearchUrl } from 'components/www/availability-search/SearchLauncher';
import { consumer } from '@tock/coretypes';
import { css } from '@emotion/react';
import {
  modifyCookiePrefsLabelText,
  onOpenCookieConsentPrefs,
} from '@tock/shared/utils/cookieConsentUtils';
import Anchor from 'components/Anchor';
import React from 'react';

interface MenuElementCommonProps {
  menuPageId: string;
  /**
   * An optional boolean that, if falsy, will prevent a given menu item from rendering (e.g., intended to be used with feature flags, etc.)
   */
  conditional?: boolean;
  jumbo?: boolean;
}

interface MenuItemAnchor extends MenuElementCommonProps {
  listComponentType: 'itemAnchor';
  listComponentProps?: {
    elementStart?: React.ReactNode;
    elementEndProps?: ListItemElementEndProps;
  };
  name: string;
  href: string;
  // This is used to check the search value of the current URL for a substring of critical interest
  searchTarget?: string;
  key: MenuItemKeys;
  additionalOnClick?: () => void;
}

interface MenuItemButton extends MenuElementCommonProps {
  listComponentType: 'itemButton';
  listComponentProps?: {
    elementStart?: React.ReactNode;
    elementEndProps?: ListItemElementEndProps;
  };
  name: string;
  navigateTo?: string;
  key: MenuItemKeys;
  additionalOnClick?: () => void;
}

interface MenuHeading extends MenuElementCommonProps {
  listComponentType: 'heading';
  name: string;
}

interface MenuMiscellaneous extends MenuElementCommonProps {
  listComponentType: 'miscellaneous';
  component: React.ReactNode;
}

interface MenuDivider extends MenuElementCommonProps {
  listComponentType: 'divider';
  key: `divider${number}`;
}

type MenuElement = MenuItemAnchor | MenuItemButton | MenuHeading | MenuDivider | MenuMiscellaneous;

enum MenuItemKeys {
  'HOME',
  'SEARCH',
  'SEARCH_DINE_IN',
  'SEARCH_DELIVERY',
  'SEARCH_PICKUP',
  'EVENTS_COLLECTION',
  'YOUR_RESERVATIONS',
  'SAVED_BUSINESSES',
  'ACCOUNT',
  'TOCK_GIFT_CARDS',
  'MOBILE_APPS',
  'INSTAGRAM',
  'TWITTER',
  'LINKED_IN',
  'FACEBOOK',
  'ABOUT_TOCK',
  'SUPPORT_COMPANY',
  'CAREERS',
  'TERMS',
  'PRIVACY',
  'ACCOUNT_INFORMATION',
  'TRANSACTIONS',
  'GIFT_CARDS_PROFILE',
  'PAYMENT_METHODS',
  'LOG_OUT',
  'CONCIERGE_PORTAL',
  'COOKIE_CONSENT',
  'SUBMIT_FEEDBACK',
  'SUBMIT_BUG',
  'YOUR_WAITLISTS',
  'JOIN',
}

export interface MobileSiteMenuImplProps {
  /**
   * Whether or not the component is rendering when a user is logged in
   */
  isLoggedIn: boolean;
  /**
   * Whether or not the menu should render partner items (e.g., Concierge portal)
   */
  isPartner: boolean;
  /**
   * The user's current metro area
   */
  metroArea?: consumer.IMetroArea;
  /**
   * Current route
   */
  pathname: string;
  /**
   * Search/query part of the url for current route, if any
   */
  search: string;
  /**
   * Patron name
   */
  patronName?: string;
  /**
   * Is the menu currently visible? this is actually tracked by state external to the
   * menu.
   */
  open: boolean;
  /**
   * Callback for closing the menu, should change the received `open` prop.
   */
  onClose(): void;
  /**
   * Link to patron profile image
   */
  profileImageLink?: string;
}

const nestedContentId = 'nested'; // ID for nested content - separated to mitigate potential for typo

/**
 * Computes properties that are relevant to a MenuItem or MenuElement when building
 * out a chooser for navigating into search results scoped to a particular type.
 *
 * This is used in this module, but also in DesktopHeaderReservationTypeSelector.
 *
 */
export const buildPropsForReservationSearchMenuItem = (
  type: consumer.OfferingSearchType,
  metroArea: consumer.IMetroArea | undefined
) => {
  const date = LocalDateTime.now().truncatedTo(ChronoUnit.HOURS).plusHours(1).toLocalDate();

  return {
    href: buildSearchUrl({ metroArea, date, guests: '2', type }),
    searchTarget: `type=${type}`,
  };
};

/**
 * Implementation of SiteMenu for Consumer mobile
 */
export const MobileSiteMenuImpl: React.FunctionComponent<MobileSiteMenuImplProps> = ({
  isLoggedIn,
  isPartner,
  pathname,
  metroArea,
  search,
  patronName,
  profileImageLink,
  open,
  onClose,
}) => {
  // TODO(guest-experience-eng): delete this in and rename computeFullWineShopDesignMenuData
  // to computeMenuData in its place.
  const menuData: MenuElement[] = React.useMemo(() => {
    const buildSearchProps = (type: consumer.OfferingSearchType) =>
      buildPropsForReservationSearchMenuItem(type, metroArea);
    return [
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        listComponentProps: {
          elementStart: (
            <IconMigrationUtil legacyIcon={<IconPrivateDining />} newIcon={<IconPlate />} />
          ),
        },
        name: 'Dine in',
        ...buildSearchProps('DINE_IN_EXPERIENCES'),
        key: MenuItemKeys.SEARCH_DINE_IN,
        jumbo: true,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        listComponentProps: {
          elementStart: (
            <IconMigrationUtil legacyIcon={<IconDeliveryScooter />} newIcon={<IconDelivery />} />
          ),
        },
        name: 'Delivery',
        ...buildSearchProps('ORDER_DELIVERY'),
        key: MenuItemKeys.SEARCH_DELIVERY,
        jumbo: true,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        listComponentProps: {
          elementStart: (
            <IconMigrationUtil legacyIcon={<IconDeliveryLegacy />} newIcon={<IconToGo />} />
          ),
        },
        name: 'Pickup',
        ...buildSearchProps('ORDER_PICKUP'),
        key: MenuItemKeys.SEARCH_PICKUP,
        jumbo: true,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        listComponentProps: {
          elementStart: <IconMigrationUtil legacyIcon={<IconEvents />} newIcon={<IconTicket />} />,
        },
        name: 'Events',
        href: '/collections/events-all',
        key: MenuItemKeys.EVENTS_COLLECTION,
        jumbo: true,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'divider',
        key: 'divider4',
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        name: 'Your reservations',
        href: '/profile/reservations',
        key: MenuItemKeys.YOUR_RESERVATIONS,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        name: 'Notify',
        href: '/profile/reservations/waitlist',
        urlToMatch: 'profile/reservations/waitlist',
        key: MenuItemKeys.YOUR_WAITLISTS,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        name: 'Saved businesses',
        href: '/profile/account/playlists',
        key: MenuItemKeys.SAVED_BUSINESSES,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemButton',
        listComponentProps: {
          elementEndProps: {
            element: (
              <IconMigrationUtil
                legacyIcon={<IconArrowRightSmall />}
                newIcon={<IconChevronSmallRight />}
              />
            ),
          },
        },
        name: 'Account',
        navigateTo: nestedContentId,
        key: MenuItemKeys.ACCOUNT,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        name: 'Concierge portal',
        href: '/concierge/teams',
        key: MenuItemKeys.CONCIERGE_PORTAL,
        conditional: isPartner,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'divider',
        key: 'divider1',
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        name: 'Use Tock at your business',
        listComponentProps: {
          elementEndProps: {
            element: <IconExternalLink />,
          },
        },
        href: `${search ? 'https://exploretock.com/join/' + search + '&attribution_source=consumer' : 'https://exploretock.com/join/?attribution_source=consumer'}`,
        key: MenuItemKeys.JOIN,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'divider',
        key: 'divider2',
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'heading',
        name: 'Community',
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        name: 'Mobile apps',
        href: '/app',
        key: MenuItemKeys.MOBILE_APPS,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        name: 'Instagram',
        href: TOCK_INSTAGRAM_URL,
        key: MenuItemKeys.INSTAGRAM,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        name: 'Twitter',
        href: TOCK_TWITTER_URL,
        key: MenuItemKeys.TWITTER,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        name: 'LinkedIn',
        href: TOCK_LINKED_IN_URL,
        key: MenuItemKeys.LINKED_IN,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        name: 'Facebook',
        href: TOCK_FACEBOOK_URL,
        key: MenuItemKeys.FACEBOOK,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'divider',
        key: 'divider3',
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'heading',
        name: 'Company',
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        name: 'Support',
        href: '/help',
        key: MenuItemKeys.SUPPORT_COMPANY,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        name: 'Careers',
        href: 'https://www.exploretock.com/join/careers',
        key: MenuItemKeys.CAREERS,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        name: 'Terms',
        href: '/terms',
        key: MenuItemKeys.TERMS,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemAnchor',
        name: 'Privacy',
        href: '/privacy',
        key: MenuItemKeys.PRIVACY,
      },
      {
        menuPageId: baseMenuId,
        listComponentType: 'itemButton',
        listComponentProps: {},
        name: modifyCookiePrefsLabelText(),
        key: MenuItemKeys.COOKIE_CONSENT,
        additionalOnClick: onOpenCookieConsentPrefs,
      },
      {
        menuPageId: nestedContentId,
        listComponentType: 'heading',
        name: 'Account',
      },
      {
        menuPageId: nestedContentId,
        listComponentType: 'itemAnchor',
        name: 'Account information',
        href: '/profile/account/profile',
        key: MenuItemKeys.ACCOUNT_INFORMATION,
      },
      {
        menuPageId: nestedContentId,
        listComponentType: 'itemAnchor',
        name: 'Transactions',
        href: '/profile/account/transactions',
        key: MenuItemKeys.TRANSACTIONS,
      },
      {
        menuPageId: nestedContentId,
        listComponentType: 'itemAnchor',
        name: 'Gift cards',
        href: '/profile/account/gift-cards',
        key: MenuItemKeys.GIFT_CARDS_PROFILE,
      },
      {
        menuPageId: nestedContentId,
        listComponentType: 'itemAnchor',
        name: 'Payment methods',
        href: '/profile/account/billing',
        key: MenuItemKeys.PAYMENT_METHODS,
      },
      {
        menuPageId: nestedContentId,
        listComponentType: 'itemAnchor',
        name: 'Log out',
        href: '/logout',
        urlToMatch: '/logout',
        key: MenuItemKeys.LOG_OUT,
      },
    ];
  }, [isPartner, metroArea, search]);

  // Filter menu data for anchor items, to be used to determine which menu item is selected
  const menuDataFilteredForAnchor = menuData.filter(
    (menuElement) => menuElement?.listComponentType === 'itemAnchor'
  ) as MenuItemAnchor[];

  // Determine which menu item is selected, and which content page to render upon menu opening
  let foundMenuElement = menuDataFilteredForAnchor.find(
    ({ href }) => href !== '/' && pathname === href
  );

  // If no exact match found, search 'includes' matches, also trimming out anything
  // after the ? in href.
  if (!foundMenuElement) {
    foundMenuElement = menuDataFilteredForAnchor.find(({ href, searchTarget }) => {
      const hrefPath = href.split('?')[0];
      return (
        href !== '/' &&
        pathname.includes(hrefPath) &&
        (!searchTarget || search.includes(searchTarget))
      );
    });
  }

  const selectedMenuItem = foundMenuElement?.key ?? MenuItemKeys.HOME;
  const initialContentId = foundMenuElement?.menuPageId ?? baseMenuId;

  // Send menu click analytics event
  const sendMenuClickAnalyticsEvent = (menuItemName: string) => {
    return window.analytics?.sendEvent(
      CATEGORY_NAV,
      ACTION_MOBILE_NAV_MENU_DESTINATION,
      menuItemName
    );
  };

  // Build site menu, per <SiteMenu>'s builder function
  const builder = React.useCallback(
    (
      setSelectedId: React.Dispatch<React.SetStateAction<string>>
    ): {
      [key: string]: React.ReactNode;
    } => {
      //Generate list items for specified menuContentPageId
      const generateListFromMenuData = (
        menuData: MenuElement[],
        targetMenuPageId: string,
        setSelectedId: React.Dispatch<React.SetStateAction<string>>
      ) => {
        return (
          <List>
            {menuData
              // Filter out any items that have a falsy conditional (items without a conditional will evaluate to true / not be filtered out)
              .filter(
                ({ conditional, menuPageId }) =>
                  menuPageId === targetMenuPageId && conditional !== false
              )
              .map((element) => {
                const { listComponentType, jumbo } = element;
                const listItemWeight = jumbo ? 'medium' : 'book';
                const listItemSize = jumbo ? 'large' : 'medium';
                switch (listComponentType) {
                  case 'itemAnchor':
                    return (
                      <ListItem
                        component={Anchor}
                        key={element.key}
                        href={element.href}
                        onClick={() => {
                          sendMenuClickAnalyticsEvent(element.name);
                          element.additionalOnClick && element.additionalOnClick();
                          onClose();
                        }}
                        size={listItemSize}
                        selected={selectedMenuItem === element.key}
                        variant="basic"
                        weight={listItemWeight}
                        {...element.listComponentProps}
                        data-testid={`${element.name.replace(/\W/g, '').toLowerCase()}-link`}
                      >
                        {element.name}
                      </ListItem>
                    );
                  case 'miscellaneous':
                    return element.component;
                  case 'itemButton':
                    return element.name ? (
                      <ListItem
                        component={'button'}
                        key={element.key}
                        onClick={() => {
                          sendMenuClickAnalyticsEvent(element.name);
                          element.navigateTo && setSelectedId(element.navigateTo);
                          element.additionalOnClick?.();
                        }}
                        size={listItemSize}
                        selected={selectedMenuItem === element.key}
                        variant="basic"
                        weight={listItemWeight}
                        {...element.listComponentProps}
                      >
                        {element.name}
                      </ListItem>
                    ) : null;
                  case 'heading':
                    return (
                      <ListHeading key={`HEADING ${element.name}`} size={listItemSize}>
                        {element.name}
                      </ListHeading>
                    );
                  case 'divider':
                    return <Divider key={element.key} position="middle" component="li" />;
                }
              })}
          </List>
        );
      };

      return {
        [baseMenuId]: (
          <>
            <SiteMenuTopLevelHeading
              onClick={onClose}
              href={'/profile'}
              pathname={pathname}
              loggedIn={isLoggedIn}
              patronName={patronName}
              profileImageLink={profileImageLink}
            />
            {generateListFromMenuData(menuData, baseMenuId, setSelectedId)}
          </>
        ),
        [nestedContentId]: (
          <>
            <SiteMenuBackButton
              onClickBackButton={() => {
                setSelectedId('baseMenu');
              }}
              text="Return to menu"
            />
            {generateListFromMenuData(menuData, nestedContentId, setSelectedId)}
          </>
        ),
      };
    },
    [menuData, selectedMenuItem, onClose, isLoggedIn, pathname, patronName, profileImageLink]
  );

  return (
    <Drawer
      css={css`
        & .MuiPaper-root {
          width: min(350px, 85%);
        }
      `}
      onClose={() => {
        if (window.analytics) {
          window.analytics.sendEvent(CATEGORY_NAV, ACTION_MOBILE_NAV_MENU, 'Menu closed');
        }
        onClose();
      }}
      open={open}
      onOpen={() => undefined}
      data-testid="mobile-menu-drawer"
    >
      <SiteMenu builder={builder} showInitialContentId={initialContentId} />
    </Drawer>
  );
};
